import { cmxDateTime } from '@codametrix/ui-common';
import type * as React from 'react';
import type {
  ActivityFeedItem,
  ActivityFilter,
  ActivityToken
} from '../../../project_starburst';
import { formatLoggedAs } from '../../../project_starburst/molecules/CaseActionModal';

// Reduces raw redux activity rows (coding: state.codeBench.activity; grading:
// state.gradeBenchPanel.activity) into the presentational `ActivityFeedItem[]` the project_starburst
// `ActivityFeed` renders. The phrasing/chips mirror the shared-views timeline formatters, but are
// reimplemented HERE (in the adapter) on purpose: the styling component lives in project_starburst
// and must not import shared-views, and shared-views itself is left untouched.

const chip = (value: React.ReactNode): ActivityToken => ({ chip: value });

const reasonChips = (reasons: unknown): ActivityToken[] => {
  if (!Array.isArray(reasons)) return [];
  const labels = reasons
    .map((r) => (r && typeof r === 'object' ? (r as { label?: unknown }).label : undefined))
    .filter((l): l is string => typeof l === 'string' && l.trim().length > 0);
  return labels.flatMap((label, i) => (i === 0 ? [chip(label)] : [' ', chip(label)]));
};
const hasReasons = (a: any): boolean => reasonChips(a.reasons).length > 0;

const REVISION_VERB: Record<string, string> = {
  ADD: 'added',
  DEL: 'removed',
  MOD: 'updated'
};

const capitalize = (s?: string | null): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

const humanizeUnderscores = (s?: string | null): string =>
  (s ?? '')
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const CODE_ENTITY_LABEL: Record<string, string> = {
  ProcedureCode: 'Procedure',
  DiagnosisCode: 'Diagnosis',
  ModifierCode: 'Modifier'
};

// "Weekday, Month D, YYYY h:mm am" — the same readable form the legacy Activity tab used.
const formatTimestamp = (timestamp?: string | null): string => {
  if (!timestamp) {
    return '';
  }
  const date = cmxDateTime.format(timestamp, 'dddd, MMMM D, YYYY');
  const time = cmxDateTime.format(
    timestamp,
    cmxDateTime.FORMATS.TIME_WITHOUT_SECONDS
  );
  return `${date} ${time}`;
};

const formatNumericDateTime = (timestamp?: string | null): string => {
  const at = timestamp ? new Date(timestamp) : undefined;
  return at && !Number.isNaN(at.getTime())
    ? formatLoggedAs(at, undefined, 'numeric')
    : '';
};

// A code chip run: optional units chip, the code chip, optional trailing "charge N" chip.
const codeWithCharge = (code: any, ...between: ActivityToken[]): ActivityToken[] => {
  const tokens: ActivityToken[] = [];
  if (code?.units) {
    tokens.push(chip(String(code.units)));
  }
  tokens.push(chip(code?.code));
  tokens.push(...between);
  if (code?.transaction?.sequenceNumber != null) {
    tokens.push(' charge ', chip(String(code.transaction.sequenceNumber)));
  }
  return tokens;
};

// ---- CQA (grading) activity rows -------------------------------------------------------------

const cqaTokens = (item: any): ActivityToken[] | null => {
  const verb = REVISION_VERB[item.revisionType] ?? 'updated';

  if ('code' in item && !('attribute' in item) && item.code) {
    const c = item.code;
    return [`${capitalize(c.entityType)} `, ...codeWithCharge(c, ` ${verb}`)];
  }

  if ('codeAssessment' in item && item.codeAssessment) {
    const ca = item.codeAssessment;
    const parent = ca.parentCode;
    const tokens: ActivityToken[] = [
      'Assessment ',
      chip(ca.assessment),
      ` ${verb} role `,
      chip(ca.role)
    ];
    if (parent) {
      tokens.push(
        ` for ${capitalize(parent.entityType)} `,
        ...codeWithCharge(parent)
      );
    } else {
      tokens.push(' n/a');
    }
    return tokens;
  }

  if (
    'attribute' in item &&
    item.attribute &&
    item.code &&
    ['CODE_PROCEDURE', 'CODE_DIAGNOSIS', 'CODE_MODIFIER'].includes(
      item.attribute.entityType
    )
  ) {
    const attr = item.attribute;
    return [
      'Question ',
      chip(attr?.questionConfiguration?.text ?? attr.questionLabel),
      ` ${verb} answered `,
      chip(attr.answerDisplayText),
      ` for ${capitalize(item.code.entityType)} `,
      ...codeWithCharge(item.code)
    ];
  }

  if (item.entityType === 'span' && 'span' in item) {
    const s = item.span;
    const isImportedDelete = s?.original && item.revisionType === 'DEL';
    const label = isImportedDelete ? 'Imported span' : 'Loose span';
    return s?.coveredText
      ? [`${label} `, chip(s.coveredText), ` ${verb}`]
      : [
          `${label} ${verb} start offset ${s?.beginOffset} end offset ${s?.endOffset}`
        ];
  }

  if ('attribute' in item && item.attribute?.entityType === 'CASE') {
    const attr = item.attribute;
    return [
      'Case question ',
      chip(attr?.questionConfiguration?.text ?? attr.questionLabel),
      ` ${verb} answered `,
      chip(attr.answerDisplayText)
    ];
  }

  if ('attribute' in item && item.attribute?.entityType === 'SPAN') {
    const attr = item.attribute;
    return [
      'Span highlight question ',
      chip(attr?.questionConfiguration?.text ?? attr.questionLabel),
      ` ${verb} answered `,
      chip(attr.answerDisplayText)
    ];
  }

  if ('codeSpan' in item && item.codeSpan) {
    const cs = item.codeSpan;
    if (cs.original && item.revisionType !== 'DEL') {
      return null;
    }
    const tokens: ActivityToken[] = [
      `${capitalize(cs.codeType)} `,
      chip(cs.code?.code),
      ` ${verb} attached to span `
    ];
    if (cs.span?.coveredText) {
      tokens.push(chip(cs.span.coveredText));
    } else {
      tokens.push(
        `start offset ${cs.span?.beginOffset} end offset ${cs.span?.endOffset}`
      );
    }
    return tokens;
  }

  return null;
};

// ---- Coding user/system activity rows --------------------------------------------------------

const userTokens = (a: any): ActivityToken[] | null => {
  const label = CODE_ENTITY_LABEL[a.codeType] ?? 'Code';
  switch (a.actionType) {
    case 'ADD_CODE':
      return [`${label} `, chip(a.code), ' added'];
    case 'REMOVE_CODE':
      return [`${label} `, chip(a.code), ' removed'];
    case 'UPDATE_CODE':
      return a.codeType === 'DiagnosisCode' || a.codeType === 'ModifierCode'
        ? [`${label} `, chip(a.code), ' reordered']
        : [`${label} `, chip(a.code), ' updated to ', chip(`${a.units} units`)];
    case 'SUBMIT_CODES':
      return a.withOverride
        ? ['Charges submitted with override']
        : ['Charges submitted'];
    case 'REFRESH_CASE':
      return ['Case refreshed'];
    case 'CANCEL_CASE':
      return ['Case cancelled'];
    case 'ADD_INVOICE':
      return ['Invoice added'];
    case 'DELETE_INVOICE':
      return ['Invoice removed'];
    case 'ADD_TRANSACTION':
      return a.sequenceNumber != null
        ? ['Charge ', chip(String(a.sequenceNumber)), ' added']
        : ['Charge added'];
    case 'DELETE_TRANSACTION':
      return a.sequenceNumber != null
        ? ['Charge ', chip(String(a.sequenceNumber)), ' removed']
        : ['Charge removed'];
    case 'UPDATE_CHARGEABLE':
      return a.isChargeable === false && hasReasons(a)
        ? ['Case excluded ', ...reasonChips(a.reasons)]
        : [`Case marked ${a.isChargeable ? 'chargeable' : 'non-chargeable'}`];
    case 'HOLD_PROCESS_INSTANCE':
      if (a.holdReason && a.holdReason !== 'OFF_HOLD') {
        return hasReasons(a)
          ? ['Case placed on hold ', ...reasonChips(a.reasons)]
          : ['Case placed on hold ', chip(humanizeUnderscores(a.holdReason))];
      }
      return ['Case taken off hold'];
    case 'UPDATE_FIELD':
      return [
        'Field ',
        chip(humanizeUnderscores(a.updatedFieldName) || '—'),
        ' set to ',
        chip(humanizeUnderscores(a.updatedFieldValue) || '—')
      ];
    case 'SPLIT_CASE':
      return ['Case split'];
    case 'MERGE_CASE':
      return ['Case merged'];
    case 'CREATE_CASE':
      return ['Case created'];
    case 'MANUAL_ASSIGNMENT':
      return hasReasons(a)
        ? ['Transferred case to ', chip(a.destinationQueueName ?? a.reassignedUsername)]
        : ['Case reassigned to ', chip(a.reassignedUsername)];
    case 'DISMISS_LATE_CASE':
      return ['Late case notice dismissed'];
    // Dropped, not rendered: the backend's generic `useractivity/v1` audit log ALSO logs a
    // COMMENT_ON_CASE row for every comment (fetchActivity fetches it in parallel alongside
    // `useractivity/comments/v1`), so rendering it here would show every comment twice — once
    // from this row and once from the dedicated `USER_ACTIVITY_COMMENTS` row below, which is the
    // source `addUserActivityComment` re-fetches and is the authoritative one for comment text.
    case 'COMMENT_ON_CASE':
      return null;
    default:
      return ['Activity ', chip(a.actionType)];
  }
};

const systemTokens = (a: any): ActivityToken[] => {
  if (a.action === 'ASSIGN_PROFILE' && a.targetEntityType === 'WORKITEM') {
    return ['Queue matched ', chip(a?.metadata?.profileName), ' for distribution'];
  }
  if (
    (a.action === 'CASE_VERSION_UPDATE' || a.action === 'LATE_CASE_UPDATE') &&
    a.targetEntityType === 'PROCESS_INSTANCE'
  ) {
    return ['Case version updated to ', chip(a?.metadata?.caseVersion)];
  }
  return ['System activity ', chip(a.action)];
};

const COMMENT_ACTIVITY_TYPES = new Set(['USER_ACTIVITY_COMMENTS', 'CQA_CODER_COMMENT']);

/** Narrows the raw redux activity rows to the Activity-tab filter chip's current selection, before
 *  `toActivityFeedItems` reduces them to presentational tokens -- `'all'` is a no-op (identity). */
export const filterActivity = (
  activity: readonly any[] | undefined,
  filter: ActivityFilter
): readonly any[] | undefined => {
  if (filter === 'all' || !activity) {
    return activity;
  }
  if (filter === 'feedback') {
    return activity.filter((item) => item?.activityType === 'CQA_FEEDBACK_COMMENT');
  }
  return activity.filter((item) => COMMENT_ACTIVITY_TYPES.has(item?.activityType));
};

const userDetail = (a: any): string[] | undefined => {
  const lines: string[] = [];
  const showNote = a.actionType === 'UPDATE_CHARGEABLE' || a.actionType === 'MANUAL_ASSIGNMENT';
  if (showNote && typeof a.message === 'string' && a.message.trim().length > 0) lines.push(a.message);
  if (typeof a.caseContext === 'string' && a.caseContext.trim().length > 0) lines.push(`Context: ${a.caseContext}`);
  return lines.length > 0 ? lines : undefined;
};

// ---- Dispatch by activityType ----------------------------------------------------------------

/**
 * Convert the raw activity rows into presentational feed items, newest-first. Rows the CQA
 * formatter intentionally drops (e.g. imported code adds) are filtered out.
 */
export const toActivityFeedItems = (
  activity: readonly any[] | undefined
): ActivityFeedItem[] => {
  const items: ActivityFeedItem[] = [];

  (activity ?? []).forEach((item, idx) => {
    const type: string = item?.activityType ?? '';
    let actor = '';
    let tokens: ActivityToken[] | null = null;
    let kind: 'user' | 'system' | 'transfer' = 'user';
    let detail: string[] | undefined;
    let subline: string | undefined;

    switch (type) {
      case 'SYSTEM':
        actor = 'System';
        kind = 'system';
        tokens = systemTokens(item);
        break;
      case 'USER':
        actor = item.username ?? '';
        tokens = userTokens(item);
        detail = userDetail(item);
        if (item.actionType === 'MANUAL_ASSIGNMENT' && hasReasons(item)) {
          kind = 'transfer';
          const reasons = item.reasons
            .map((reason: { label?: unknown }) => reason.label)
            .filter((label: unknown): label is string => typeof label === 'string' && label.trim().length > 0);
          subline = `${formatNumericDateTime(item.timestamp)} · Reason: ${reasons.join(', ')}`;
        }
        break;
      case 'CQA':
        actor = item.revInfo?.username ?? '';
        tokens = cqaTokens(item);
        break;
      case 'USER_ACTIVITY_COMMENTS':
        actor = item.username ?? '';
        tokens = ['Comment added ', chip(item.message)];
        break;
      case 'CQA_CODER_COMMENT':
        actor = item.coder_fullName || item.coder_username || '';
        tokens = ['Comment added ', chip(item.note_content)];
        break;
      case 'CQA_FEEDBACK_COMMENT':
        actor = item.coder_fullName || item.coder_username || '';
        tokens = ['Feedback added ', chip(item.note_content)];
        break;
      // Unknown activityType: `tokens` stays null and the row is dropped below.
    }

    if (!tokens) {
      return;
    }

    items.push({
      id: `${item.timestamp ?? ''}-${idx}`,
      actor,
      timestamp: formatTimestamp(item.timestamp),
      tokens,
      kind,
      ...(subline ? { subline } : {}),
      ...(detail ? { detail } : {})
    });
  });

  return items;
};
